// src/hooks/useLocation.js
import { useEffect, useState } from "react";
import CountryStateCityAPI from "../services/countryStateCityService";

export default function useLocation() {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize API instance
  const api = new CountryStateCityAPI('RzZtOFR3TGZUT2ZuOThnSGdqVzJOSTNoZU1YUlB1eWhqM29lb09qYg==');

  // Helper function to find country ISO2 code by name
  const getCountryCode = (countryName) => {
    const country = countries.find(c => c.name === countryName);
    return country ? country.iso2 : null;
  };

  // Helper function to find state ISO2 code by name
  const getStateCode = (stateName) => {
    const state = states.find(s => s.name === stateName);
    return state ? state.iso2 : null;
  };

  // fetch countries
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoading(true);
        setError(null);
        const countriesData = await api.getCountries();
        setCountries(countriesData);
      } catch (error) {
        console.error('Failed to load countries:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    loadCountries();
  }, []);

  // fetch states when country changes
  useEffect(() => {
    if (!selectedCountry) {
      setStates([]);
      setSelectedState("");
      setCities([]);
      setSelectedCity("");
      return;
    }

    const loadStates = async () => {
      try {
        setLoading(true);
        setError(null);
        const countryCode = getCountryCode(selectedCountry);
        if (!countryCode) {
          console.error('Country code not found for:', selectedCountry);
          setError(new Error('Country code not found'));
          return;
        }
        const statesData = await api.getStates(countryCode);
        setStates(statesData);
        setSelectedState("");
        setCities([]);
        setSelectedCity("");
      } catch (error) {
        console.error('Failed to load states:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    loadStates();
  }, [selectedCountry, countries]);

  // fetch cities when state changes
  useEffect(() => {
    if (!selectedCountry || !selectedState) {
      setCities([]);
      setSelectedCity("");
      return;
    }

    const loadCities = async () => {
      try {
        setLoading(true);
        setError(null);
        const countryCode = getCountryCode(selectedCountry);
        const stateCode = getStateCode(selectedState);
        
        if (!countryCode) {
          console.error('Country code not found for:', selectedCountry);
          setError(new Error('Country code not found'));
          return;
        }
        
        if (!stateCode) {
          console.error('State code not found for:', selectedState);
          setError(new Error('State code not found'));
          return;
        }
        
        const citiesData = await api.getCities(countryCode, stateCode);
        setCities(citiesData);
        setSelectedCity("");
      } catch (error) {
        console.error('Failed to load cities:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    loadCities();
  }, [selectedCountry, selectedState, countries, states]);

  return {
    countries,
    states,
    cities,
    selectedCountry,
    selectedState,
    selectedCity,
    setSelectedCountry,
    setSelectedState,
    setSelectedCity,
    loading,
    error,
  };
}
